
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, usePaginatedQuery } from 'convex/react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { App } from '@/types/App';
import { closeSKOverlay, showSKOverlay } from '@/utils/nativeInterface';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { toast } from '@/hooks/use-toast';
import { api } from '../../api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface AppDetailHeaderProps {
  app: App;
  installPressed?: boolean;
  onInstallPress?: () => void;
  onBack?: () => void;
}

const AppDetailHeader: React.FC<AppDetailHeaderProps> = ({ app, installPressed = false, onInstallPress, onBack }) => {
  const navigate = useNavigate();
  const { serial, source } = useEffectiveSerial();
  const installApp = useMutation(api.devices.apps.install);
  const [isInstalling, setIsInstalling] = useState(false);

  // Preload available apps to resolve Convex app id from trackId
  // Try device-specific first, fall back to all apps if device not found
  const deviceSpecific = usePaginatedQuery(
    (api.apps.list as any).allowedByDeviceFilter,
    serial ? { serial } : "skip",
    { initialNumItems: 200 }
  );
  
  const fallbackAll = usePaginatedQuery(
    api.apps.list.all,
    !serial || (deviceSpecific.status === 'Exhausted' && !deviceSpecific.results?.length) ? {} : "skip",
    { initialNumItems: 200 }
  );
  
  const available = deviceSpecific.results?.length ? deviceSpecific : fallbackAll;

  const convexAppIdFromList = useMemo(() => {
    const results = (available as any)?.results as any[] | undefined;
    if (!results) return undefined;
    const match = results.find((r: any) => String(r.trackId) === String(app.appStoreId));
    return match?.id;
  }, [available.results, app.appStoreId]);

  console.log('AppDetailHeader - Serial Resolution:', { serial, source });

  const handleBackNavigation = async () => {
    console.log('Back button clicked');
    try {
      await closeSKOverlay();
      console.log('SKOverlay closed successfully');
    } catch (error) {
      console.error('Error closing SKOverlay:', error);
    }
    
    if (onBack) {
      onBack();
    } else {
      // Fallback to navigation for direct URL access
      navigate('/', { replace: true });
    }
  };

  const handleTroubleshootingAction = async () => {
    if (app?.appStoreId) {
      console.log('Triggering SKOverlay from troubleshooting popup for app:', app.name);
      try {
        await showSKOverlay(app.appStoreId);
      } catch (error) {
        console.error('Error showing SKOverlay from troubleshooting:', error);
      }
    }
  };

  const handleInstall = async () => {
    console.log('=== INSTALL BUTTON DEBUG ===');
    console.log('Install button clicked for app:', app.name);
    console.log('App Store ID:', app.appStoreId);
    console.log('Install pressed state:', installPressed);
    console.log('Serial:', serial);
    console.log('Serial source:', source);
    console.log('App ID:', app.id);
    
    // Validate app ID is present
    if (!app.id) {
      console.error('No app ID available');
      toast({
        title: "Installation Error",
        description: "App ID is missing. Please try again.",
        variant: "destructive"
      });
      return;
    }
    
    if (!serial) {
      console.error('No device serial available');
      toast({
        title: "Device Error",
        description: "Device serial is missing. Please try again.",
        variant: "destructive"
      });
      return;
    }
    
    // Set loading state
    setIsInstalling(true);
    
    try {
      let convexAppId: any = app.id as any;

      // If the app id looks like an App Store trackId, resolve Convex id from available apps
      if (/^\d+$/.test(app.id)) {
        if (!convexAppIdFromList) {
          console.error('No matching Convex app found for trackId', app.appStoreId);
          toast({
            title: "App Not Available",
            description: "This app is not available for this device.",
            variant: "destructive"
          });
          return;
        }
        convexAppId = convexAppIdFromList as any;
      }

      console.log('Calling devices.apps.install API...');
      await installApp({
        app: convexAppId,
        serial: serial
      });
      console.log('devices.apps.install API call completed successfully');
      
      // Call the onInstallPress callback
      if (onInstallPress) {
        console.log('Calling onInstallPress...');
        onInstallPress();
      }
      
      // SKOverlay is triggered by parent via onInstallPress to avoid duplicate calls
      
    } catch (error) {
      console.error('Error during install process:', error);
      
      // Provide specific error messages based on error type
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      if (errorMessage.includes('not found') || errorMessage.includes('does not exist')) {
        toast({
          title: "App Not Available",
          description: "This app is not available in our database. Please try another app.",
          variant: "destructive"
        });
      } else if (errorMessage.includes('permission') || errorMessage.includes('denied')) {
        toast({
          title: "Permission Denied",
          description: "You don't have permission to install apps on this device.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Installation Failed",
          description: "Unable to install the app. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setIsInstalling(false);
    }
    
    console.log('=== END INSTALL BUTTON DEBUG ===');
  };

  return (
    <div 
      className="bg-background/80 backdrop-blur-md flex-shrink-0 touch-none"
      style={{ touchAction: 'none' }}
    >
      <div className="p-4 pb-0">
        {/* Back Button */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={handleBackNavigation}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Button>
        </div>
        
        {/* App Header */}
        <div className="flex items-center space-x-4 mb-4">
          <img
            src={app.icon}
            alt={`${app.name} icon`}
            className="w-16 h-16 rounded-xl object-cover bg-muted shadow-sm"
          />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-foreground mb-1">
                  {app.name}
                </h2>
                <p className="text-muted-foreground">
                  {app.category || 'Uncategorized'}
                </p>
              </div>
              <Button
                onClick={handleInstall}
                className="h-8 px-6 text-sm font-semibold rounded-full bg-blue-500 hover:bg-blue-600 text-white border-0 data-[state=pressed]:bg-gray-400"
                disabled={(!app.appStoreId || app.appStoreId.trim() === '') || installPressed || isInstalling}
                variant={installPressed ? "secondary" : "default"}
                style={installPressed ? { 
                  backgroundColor: '#9CA3AF', 
                  color: 'white',
                  borderColor: 'transparent'
                } : {
                  backgroundColor: '#3B82F6',
                  borderColor: 'transparent'
                }}
              >
                {isInstalling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Installing...
                  </>
                ) : installPressed ? (
                  "Continue Below"
                ) : (!app.appStoreId || app.appStoreId.trim() === '') ? (
                  "Not Available"
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-1" />
                    Install
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppDetailHeader;
